import initializeExtendedCardForConversationReadingPane from './initializeExtendedCardForConversationReadingPane';
import { createItemPartViewState } from './loadConversationReadingPane';
import { setIsExpandedMutator } from 'owa-mail-reading-pane-store/lib/actions/setIsExpanded';
import tryAddOofNodesInRollUp from './rollUp/tryAddOofNodesInRollUp';
import tryInitializeShouldShowCLPLabel from './tryInitializeShouldShowCLPLabel';
import type ConversationReadingPaneViewState from 'owa-mail-reading-pane-store/lib/store/schema/ConversationReadingPaneViewState';
import type FocusedItemPart from 'owa-mail-reading-pane-store/lib/store/schema/FocusedItemPart';
import { FocusedItemArea } from 'owa-mail-reading-pane-store/lib/store/schema/FocusedItemPart';
import type ItemPartViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemPartViewState';
import getConversationReadingPaneViewState from '../utils/getConversationReadingPaneViewState';
import itemHasHighlightTerms from '../utils/itemHasHighlightTerms';
import { getUnsupportedItemIdFromConversation } from 'owa-mail-reading-pane-store/lib/utils/unsupportedItemUtils';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import { setShowDecryptedContent } from 'owa-mail-reading-pane-store/lib/mutators/setShowDecryptedContent';
import { isFeatureEnabled } from 'owa-feature-flags';
import isCLPEnabled from 'owa-mail-protection/lib/utils/clp/isCLPEnabled';
import type { ConversationReadingPaneNode, ClientItem } from 'owa-mail-store';
import type ConversationItemParts from 'owa-mail-store/lib/store/schema/ConversationItemParts';
import mailStore from 'owa-mail-store/lib/store/Store';
import { findItemToLoad } from 'owa-mail-store/lib/utils/conversationsUtils';
import { yieldNow } from 'owa-task-queue/lib/schedule';
import { expandCollapseFossilizedTextMutator } from './expandCollapseFossilizedTextMutator';
import { setItemPartInConversationViewStateMutator } from '../mutators/setItemPartInConversationViewState';
import {
    updateLoadingStateMutator,
    updateUnsupportedItemIdMutator,
    updateCurrentSelectedItemPartMutator,
} from '../mutators/loadedConversationViewStateMutators';
import type Message from 'owa-service/lib/contract/Message';

function setItemPartExpandedState(
    isNewestOnBottom: boolean,
    hasExpandedNonDraftItem: boolean,
    loadedConversationItemParts: ItemPartViewState[],
    conversationNodes: Map<string, ConversationReadingPaneNode>
) {
    const itemPartCount = loadedConversationItemParts.length;

    if (itemPartCount == 0 || hasExpandedNonDraftItem) {
        return;
    }
    /*
     ** Expand the latest local one if no item is expanded.
     ** The latest local message can also be an OOF message, for which ItemPartViewState does not exist
     ** In that case, we expand the trigger message for the OOF message
     */
    // We traverse the array from end to start if newest on bottom;
    // from start to end otherwise.
    let itemPartToExpand: ItemPartViewState | null = null;
    const latestItemIndex = isNewestOnBottom ? itemPartCount - 1 : 0;
    let itemIndex = latestItemIndex;
    const indexIncrement = isNewestOnBottom ? -1 : 1;
    const nodeHasLocalItem = (nodeId: string): boolean => {
        const node = conversationNodes.get(nodeId);

        if (node) {
            return findItemToLoad(node)?.[1] ?? false;
        }

        return false;
    };
    while (itemIndex >= 0 && itemIndex < itemPartCount) {
        const itemPartViewState = loadedConversationItemParts[itemIndex];
        const shouldExpandOOFRollupTrigger =
            itemPartViewState.oofRollUpViewState.oofReplyNodeIds.some(nodeHasLocalItem);
        // Expand the latest local item. If the latest item is an OOF item, expand its trigger message
        if (
            !itemPartViewState.isExpanded &&
            (itemPartViewState.isLocal || shouldExpandOOFRollupTrigger)
        ) {
            itemPartToExpand = itemPartViewState;
            break;
        }
        itemIndex += indexIncrement;
    }
    // If no item part is found to be expanded after for loop, blindly expand the latest one
    if (!itemPartToExpand) {
        itemPartToExpand = loadedConversationItemParts[latestItemIndex];
    }
    setIsExpandedMutator(itemPartToExpand, true);

    expandCollapseFossilizedTextMutator(itemPartToExpand, true);

    // for newest on top, the last item in the list should have isFossilizedTextExpanded = true
    if (!isNewestOnBottom) {
        const lastItemPart = loadedConversationItemParts[itemPartCount - 1];
        if (lastItemPart) {
            expandCollapseFossilizedTextMutator(lastItemPart, true);
        }
    }
}

/**
 * Order of precedence for selecting the item part:
 * 1. If itemIdToScrollTo is provided
 * 2. First unread item
 * 3. First expanded OOF rollup
 * 4. Most recent item
 */
function setItemPartSelectedStateV2(
    loadedConversationItemParts: ItemPartViewState[],
    isNewestOnBottom: boolean,
    itemIdToScrollTo?: string
): FocusedItemPart | null {
    const itemPartCount = loadedConversationItemParts.length;
    // return null if itemPartCount is 0.
    // itemPartCount could be 0 when all conversationNodes return from GCI response are deleted drafts
    // and we're not in delete folder
    if (itemPartCount == 0) {
        return null;
    }

    let itemPartToExpand: ItemPartViewState | null = null;
    let selectedItemPart: FocusedItemPart | null = null;

    for (let i = 0; i < itemPartCount; i++) {
        const itemPartViewState = loadedConversationItemParts[i];
        const itemViewStateId = itemPartViewState.itemId;
        if (itemIdToScrollTo) {
            if (itemViewStateId == itemIdToScrollTo) {
                // When we find it, store it to expand after the loop
                itemPartToExpand = itemPartViewState;
                selectedItemPart = {
                    itemPart: itemPartViewState,
                    focusedItemArea: FocusedItemArea.Item,
                };
                break;
            }
        } else if (itemPartViewState.isExpanded) {
            const item = mailStore().items.get(itemViewStateId) as Message;
            if (item && !item.IsRead) {
                selectedItemPart = {
                    itemPart: itemPartViewState,
                    focusedItemArea: FocusedItemArea.Item,
                };
                break;
            }
        } else if (itemPartViewState.oofRollUpViewState.isOofRollUpExpanded) {
            selectedItemPart = {
                itemPart: itemPartViewState,
                focusedItemArea: FocusedItemArea.Oof,
            };
            break;
        }
    }

    // If we found an item part to expand, expand it now (outside the loop)
    if (itemPartToExpand) {
        setIsExpandedMutator(itemPartToExpand, true);
    }

    // If no specific item part was selected, return the default (most recent)
    if (!selectedItemPart) {
        selectedItemPart = {
            itemPart: loadedConversationItemParts[isNewestOnBottom ? itemPartCount - 1 : 0],
            focusedItemArea: FocusedItemArea.Item,
        };
    }

    return selectedItemPart;
}

function setItemPartSelectedState(
    loadedConversationItemParts: ItemPartViewState[],
    itemIdToScrollTo?: string
): FocusedItemPart | null {
    // Select the first expanded item
    const itemPartCount = loadedConversationItemParts.length;

    let itemPartToExpand: ItemPartViewState | null = null;

    for (let i = 0; i < itemPartCount; i++) {
        const itemPartViewState = loadedConversationItemParts[i];
        const itemViewStateId = itemPartViewState.itemId;
        if (itemIdToScrollTo) {
            // If we have an itemId to scroll to, look for the corresponding item part.
            if (itemViewStateId == itemIdToScrollTo) {
                // When we find it, store it to expand after the loop
                itemPartToExpand = itemPartViewState;
                break;
            }
        } else if (itemPartViewState.isExpanded) {
            // Or look for the first expanded item.
            return {
                itemPart: itemPartViewState,
                focusedItemArea: FocusedItemArea.Item,
            };
        } else if (itemPartViewState.oofRollUpViewState.isOofRollUpExpanded) {
            // Or look for the first item with expanded oof rollup
            return {
                itemPart: itemPartViewState,
                focusedItemArea: FocusedItemArea.Oof,
            };
        }
    }

    // If we found an item part to expand, expand it now (outside the loop)
    if (itemPartToExpand) {
        setIsExpandedMutator(itemPartToExpand, true);
        return {
            itemPart: itemPartToExpand,
            focusedItemArea: FocusedItemArea.Item,
        };
    }

    // return null if itemPartCount is 0.
    // itemPartCount could be 0 when all conversationNodes return from GCI response are deleted drafts
    // and we're not in delete folder
    return null;
}

/**
 * @returns current selected item id.
 */
function loadItemPartStates(
    loadedConversation: ConversationItemParts,
    loadedConversationViewState: ConversationReadingPaneViewState,
    conversationNodes: Map<string, ConversationReadingPaneNode>,
    items: Map<string, ClientItem>
): FocusedItemPart | null {
    const loadedConversationItemParts: ItemPartViewState[] = [];
    const { itemPartsMap, itemIdToScrollTo } = loadedConversationViewState;
    const mailboxInfo = loadedConversation.conversationId.mailboxInfo;
    let currentSelectedItemPart: FocusedItemPart | null = null;
    const loadedConversationNodeIds = loadedConversation.conversationNodeIds;
    if (loadedConversationNodeIds) {
        const shouldShowLoadingSpinner = loadedConversationNodeIds.length === 1;
        for (const nodeId of loadedConversationNodeIds) {
            const itemPartViewState = createItemPartViewState(
                {
                    mailboxInfo,
                    Id: nodeId,
                },
                shouldShowLoadingSpinner
            );
            if (itemPartViewState) {
                setItemPartInConversationViewStateMutator(
                    loadedConversationViewState,
                    nodeId,
                    itemPartViewState
                );
            }
        }

        isCLPEnabled(mailboxInfo) && tryInitializeShouldShowCLPLabel(loadedConversationViewState);

        tryAddOofNodesInRollUp(
            itemPartsMap,
            conversationNodes,
            loadedConversation.conversationNodeIds,
            false /*shouldCleanUpChildrenNodes*/
        );

        // After the calculation for oof roll up and calendar roll up, some nodes may not be existed in itemPartsMap
        // So we need to loop the conversation node ids again to find out all the normal item parts from itemPartsMap
        // for the further calcuation(Expanded state)
        let hasExpandedNonDraftItem = false;

        loadedConversationNodeIds.forEach(nodeId => {
            const itemPartViewState = itemPartsMap.get(nodeId);
            if (itemPartViewState && !itemPartViewState.isInRollUp) {
                loadedConversationItemParts.push(itemPartViewState);
                const item = items.get(itemPartViewState.itemId);

                if (item && itemHasHighlightTerms(item)) {
                    setIsExpandedMutator(itemPartViewState, true);
                }

                // Initial state for decrypted content display logic on loading the conversation item part
                /* eslint-disable-next-line owa-custom-rules/require-undefined-parameter -- (https://aka.ms/OWALintWiki)
                 * Flight checks that supply MailboxInfo should be defined as AccountFeatureName value and should be checked using isAccountFeatureEnabled to ensure consistent checking.
                 *	> The parameter mailboxInfo must be undefined. Feature flight: 'rp-encryptedEmail2ClickPreview' */
                if (isFeatureEnabled('rp-encryptedEmail2ClickPreview', mailboxInfo)) {
                    const viewState = getItemReadingPaneViewState(item?.ItemId?.Id);

                    if (item && viewState) {
                        setShowDecryptedContent(viewState, false);
                    }
                }

                hasExpandedNonDraftItem =
                    hasExpandedNonDraftItem || (itemPartViewState?.isExpanded && !item?.IsDraft);
            }
        });

        const isNewestOnBottom =
            loadedConversation.conversationSortOrder == 'ChronologicalNewestOnBottom';

        setItemPartExpandedState(
            isNewestOnBottom,
            hasExpandedNonDraftItem,
            loadedConversationItemParts,
            conversationNodes
        );

        currentSelectedItemPart =
            isFeatureEnabled('rp-expand-all') && mailStore().shouldExpandAllItemParts
                ? setItemPartSelectedStateV2(
                      loadedConversationItemParts,
                      isNewestOnBottom,
                      itemIdToScrollTo
                  )
                : setItemPartSelectedState(loadedConversationItemParts, itemIdToScrollTo);

        initializeExtendedCardForConversationReadingPane(
            loadedConversationViewState,
            loadedConversationItemParts,
            mailboxInfo
        );

        if (loadedConversationItemParts.length) {
            const rootNodeIndex = isNewestOnBottom ? 0 : loadedConversationNodeIds.length - 1;
            const rootNodeId = loadedConversationNodeIds[rootNodeIndex];

            if (loadedConversation.canLoadMore) {
                // If the conversation could load more, the see more button should be
                // bundled with the root node.
                loadedConversationViewState.nodeIdBundledWithSeeMoreMessages = rootNodeId;
            }
        }
    }
    return currentSelectedItemPart;
}

export interface FirstLoadConversationReadingPaneState {
    loadedConversation: ConversationItemParts | undefined;
    conversationReadingPaneState: ConversationReadingPaneViewState;
    conversationNodes: Map<string, ConversationReadingPaneNode>;
    items: Map<string, ClientItem>;
}

export const firstLoadConversationReadingPane = async (
    conversationId: string,
    state: FirstLoadConversationReadingPaneState = {
        loadedConversation: mailStore().conversations.get(conversationId),
        conversationNodes: mailStore().conversationNodes,
        conversationReadingPaneState: getConversationReadingPaneViewState(conversationId),
        items: mailStore().items,
    }
): Promise<void> => {
    if (
        state.conversationReadingPaneState?.conversationId?.Id != conversationId ||
        !state.loadedConversation
    ) {
        // If a different conversation has been loaded before this action is called, skip.
        return;
    }
    const loadedConversationState = state.conversationReadingPaneState;
    updateLoadingStateMutator(
        loadedConversationState,
        false /*isLoading*/,
        !!state.loadedConversation.loadingState.hasLoadFailed
    );
    if (!loadedConversationState.loadingState.hasLoadFailed) {
        const unsupportedItemId = getUnsupportedItemIdFromConversation(conversationId);
        if (unsupportedItemId) {
            updateUnsupportedItemIdMutator(loadedConversationState, unsupportedItemId);
        } else {
            const currentSelectedItemPart = loadItemPartStates(
                state.loadedConversation,
                loadedConversationState,
                state.conversationNodes,
                state.items
            );

            if (currentSelectedItemPart) {
                updateCurrentSelectedItemPartMutator(
                    loadedConversationState,
                    currentSelectedItemPart /*initiallySelectedItemPart */,
                    currentSelectedItemPart /* focusedItemPart */
                );
            }

            // LOAF optimization: yield to allow main task to complete before we start loading attachments
            await yieldNow('firstLoadConversationReadingPane');
        }
    }
};
